import { MouseEvent, useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from 'react-intl';
import TagManager from 'react-gtm-module';
import { Container, Grid, Button, AppBar, Toolbar, Box, Typography } from '@mui/material'
import { Person } from '@mui/icons-material';
import GolferMenu from './GolferMenu';
import { HtmlTooltip } from './HtmlTooltip';
import * as GolferHelper from '../library/GolferHelper';
import * as TutorialHelper from '../library/TutorialHelper';
import { GappingSequences } from '../data/GappingSequence';
import Golfer, { Loft, LoftShots } from '../classes/Golfer';
import * as ShotSelection from '../classes/ShotSelection';

export default function InBag() {
    const navigate = useNavigate();
    const intl = useIntl();
    const [golfer, setGolfer] = useState(new Golfer());
    const [pageIsValid, setPageIsValid] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [showRecommendedLoftsTitle, setShowRecommendedLoftsTitle] = useState(false);
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialId, setTutorialId] = useState(0);
    const gappingSequences = new GappingSequences();
    const intlLoftsSelect = intl.formatMessage({ id: "wedge.lofts.select" })

    useEffect(() => {
        const getAsyncData = async () => {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_inbag'
                }
            })

            setShowTutorial(await TutorialHelper.isActive());
            let newGolfer = await GolferHelper.getCurrentGolfer();
            if (newGolfer.recommendedLofts.length > 0) {
                setShowRecommendedLoftsTitle(true);
            }
            setGolfer(newGolfer);
        }

        getAsyncData();
    }, []);

    //user click their preferred loft type buttons
    const handleLoftButton = (event: MouseEvent<HTMLButtonElement>): void => {
        //assign the loft just clicked
        let newGolfer = { ...golfer, [event.currentTarget.name]: event.currentTarget.value } as Golfer;

        //get the recommended gapping sequence from Greg's data
        let gapping = gappingSequences.data.find(gs => gs.pwLoft == newGolfer.pwLoft && gs.highestLoft == newGolfer.highestLoft);
        if (gapping) {
            //first turn on the spinner and clear out previous recommendations
            newGolfer = { ...newGolfer, recommendedLofts: [] } as Golfer;
            setGolfer(newGolfer);

            setShowSpinner(true);
            setTimeout(() => {
                //now turn off the spinner and update with the actual loft recommendations
                setShowRecommendedLoftsTitle(true); // after the first set of lofts has been selected, keep the Recommended Lofts title visible
                setShowSpinner(false);
                let gaps = gapping!.gapping.split(' ');
                newGolfer = { ...newGolfer, recommendedLofts: gaps as Loft[] } as Golfer;
                setGolfer(newGolfer);
                validatePage(newGolfer); //refresh error messages on page
            }, 2000)
        } else {
            setGolfer(newGolfer);
        }
    }

    const handleRecommendedLoftButton = (event: MouseEvent<HTMLButtonElement>): void => {
        let newLoft = event.currentTarget.value as Loft;
        let newLofts = golfer.recommendedLofts;
        //toggle the button on/off by adding/removing the loft from the list
        if (isInRecommendedLofts(newLoft)) {
            //remove it
            newLofts = newLofts.filter(l => l != newLoft);
        } else {
            //add it
            newLofts.push(newLoft);
        }
        let newGolfer = { ...golfer, recommendedLofts: newLofts } as Golfer;
        validatePage(newGolfer); //refresh error messages on page
        setGolfer(newGolfer);
    }

    const isInRecommendedLofts = (loft: Loft): boolean => {
        let result = false;
        if (golfer && golfer.recommendedLofts && golfer.recommendedLofts.length > 0) {
            result = golfer.recommendedLofts.indexOf(loft) > -1;
        }
        return result;
    }

    const handleSubmit = async (event: MouseEvent<HTMLButtonElement>) => {
        var newGolfer = { ...golfer };
        //get recommended lofts based on their selections so we know which ones the golfer kept and which were changed
        let gapping = gappingSequences.data.find(gs => gs.pwLoft == newGolfer.pwLoft && gs.highestLoft == newGolfer.highestLoft);
        let recommendedLofts = new Array<any>();
        if (gapping) {
            newGolfer.recommendedLofts.forEach(loft => {
                recommendedLofts.push({
                    loft: loft,
                    was_recommended: (gapping?.gapping.includes(loft))
                })
            })
        }
        recommendedLofts = recommendedLofts.sort((a,b) => a.loft.localeCompare(b.loft));
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_fit_wedge_lofts_next',
                q_pitching_loft: newGolfer.pwLoft,
                q_highest_loft: newGolfer.highestLoft,
                recommended_loft: recommendedLofts
            }
        });
        let isValid = validatePage(golfer);
        setShowErrors(!isValid);
        if (isValid) {
            //create a list of the chosen shots for each recommended loft
            var loftShots = [] as LoftShots[];
            //sort the lofts b/c they will get out of order if user is playing around with adding/removing different lofts
            newGolfer.recommendedLofts.sort((a, b) => a.localeCompare(b));
            newGolfer.recommendedLofts.forEach((l, ix) => {
                let loftShot = new LoftShots(ix+1, l, ShotSelection.DefaultShotSelection.get(l) as ShotSelection.ShotSelection[]);
                // loftShot.loft = l;
                // loftShot.shots = DefaultShotSelection.get(l) as ShotSelection[];
                loftShots.push(loftShot);
            })
            newGolfer = { ...newGolfer, selectedLofts: loftShots };
            setGolfer(newGolfer);
            await GolferHelper.upsertGolfer(newGolfer);
            navigate('/apps/wedgefitting/selectshots');
        }
    }

    const handleBack = async () => {
        await GolferHelper.upsertGolfer(golfer);
        navigate('/apps/wedgefitting/golferpreferences');
    }

    const validatePage = (golfer: Golfer): boolean => {
        let isValid = golfer.pwLoft != '' && golfer.highestLoft != '' && golfer.recommendedLofts.length > 0;

        setPageIsValid(isValid); //
        return isValid;
    }

    const showPopup = (id: number) => {
        return showTutorial && tutorialId == id;
    }

    const handleTutorialNext = async () => {
        if (tutorialId === 1) {
            navigate('/apps/wedgefitting/selectshots');
        } else {
            let newGolfer = golfer;
            newGolfer.recommendedLofts = gappingSequences.data.find(gs => gs.pwLoft == newGolfer.pwLoft && gs.highestLoft == newGolfer.highestLoft)!.gapping.split(' ') as Loft[];
            setGolfer(newGolfer);
            setTutorialId(tutorialId + 1);
        }
    }

    const handleTutorialCancel = async () => {
        await TutorialHelper.disable();
        setShowTutorial(false);
        navigate('/apps/wedgefitting/golferinfo');
    }

    const cbGolferUpdate = async (cbGolfer: Golfer) => {
        let newGolfer = { ...golfer, firstName: cbGolfer.firstName, lastName: cbGolfer.lastName, fullName: cbGolfer.fullName, email: cbGolfer.email, phone: cbGolfer.phone, smsOptIn: cbGolfer.smsOptIn  };
        setGolfer(newGolfer);
        await GolferHelper.upsertGolfer(newGolfer);
    }

    return (
        <>
            <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'flex' } }} className='navbar'>
                <AppBar position="fixed" className='appbar'>
                    <Container maxWidth="lg">
                        <Toolbar>
                            <Grid container>
                                <Grid item xs={4}>
                                    <Typography className='name' variant="h6" component="div" sx={{ display: { xs: 'none', md: 'flex' }, marginTop: '10px' }}>
                                        <Person sx={{ width: '30px', marginRight: '5px', marginTop: '-10px', height: '2em' }}></Person>
                                        <GolferMenu golfer={golfer} cbgolferupdate={cbGolferUpdate}></GolferMenu>
                                    </Typography>

                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="h6" component="div" className='title' sx={{ display: 'flex', justifyContent: 'center', marginTop: '7px' }}>
                                        <FormattedMessage id="wedge.lofts.title" />
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} container justifyContent="flex-end">
                                    <Button disableRipple type="submit" variant="contained" onClick={handleBack} className='cancel-button'><FormattedMessage id="general.back" /></Button>
                                    <Button disableRipple type="submit" variant="contained" onClick={handleSubmit} className='submit-button'><FormattedMessage id="general.next" /></Button>
                                </Grid>
                            </Grid>
                        </Toolbar>
                    </Container>
                </AppBar>
            </Box>


            <div className="in-bag-form">
                <Container maxWidth='lg' sx={{ marginTop: '80px' }}>
                    <Grid container className='row-error'>
                        <Grid item sm={12} className='form-error-label'><span>{`${!pageIsValid && showErrors ? intlLoftsSelect : ""}`}</span>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>

                        <HtmlTooltip
                            title={
                                <>
                                    <Typography color="inherit"><FormattedMessage id="quick.start.starting.pw" /></Typography>
                                    <br />
                                    <div style={{ textAlign: 'right' }}>
                                        <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                        <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                    </div>
                                </>
                            }
                            open={showPopup(0)}
                            placement={'bottom-end'}
                            arrow={false}
                            maxwidth={400}
                        >


                            <Grid item md={6} sm={12}>
                                <Grid item className='form-label' sx={{ marginBottom: '10px' }}>
                                    <span><FormattedMessage id="wedge.lofts.pitching" /></span>
                                </Grid>
                                <Grid container className='form-row-buttons'>
                                    <Grid item sm={2} sx={{ marginBottom: '10px' }}>
                                        <Button disableRipple fullWidth name='pwLoft' value='43' variant="outlined" className={`short ${golfer.pwLoft == "43" as Loft ? "selected" : ""}`} onClick={handleLoftButton}>43</Button>
                                    </Grid>
                                    <Grid item sm={2} sx={{ marginBottom: '10px' }}>
                                        <Button disableRipple fullWidth name='pwLoft' value='44' variant="outlined" className={`short ${golfer.pwLoft == "44" as Loft ? "selected" : ""}`} onClick={handleLoftButton}>44</Button>
                                    </Grid>
                                    <Grid item sm={2} sx={{ marginBottom: '10px' }}>
                                        <Button disableRipple fullWidth name='pwLoft' value='45' variant="outlined" className={`short ${golfer.pwLoft == "45" as Loft ? "selected" : ""}`} onClick={handleLoftButton}>45</Button>
                                    </Grid>
                                    <Grid item sm={2} sx={{ marginBottom: '10px' }}>
                                        <Button disableRipple fullWidth name='pwLoft' value='46' variant="outlined" className={`short ${golfer.pwLoft == "46" as Loft ? "selected" : ""}`} onClick={handleLoftButton}>46</Button>
                                    </Grid>
                                    <Grid item sm={2} sx={{ marginBottom: '10px' }}>
                                        <Button disableRipple fullWidth name='pwLoft' value='47' variant="outlined" className={`short ${golfer.pwLoft == "47" as Loft ? "selected" : ""}`} onClick={handleLoftButton}>47</Button>
                                    </Grid>
                                    <Grid item sm={2} sx={{ marginBottom: '10px' }}>
                                        <Button disableRipple fullWidth name='pwLoft' value='48' variant="outlined" className={`short ${golfer.pwLoft == "48" as Loft ? "selected" : ""}`} onClick={handleLoftButton}>48</Button>
                                    </Grid>
                                </Grid>
                                <Grid container className='row'>
                                    <Grid item sm={2}>
                                        <Grid container className='row'>
                                            <Grid item sm={12}><span>T250</span></Grid>
                                        </Grid>
                                        <Grid container className='row'>
                                            <Grid item sm={12}><span>T350</span></Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item sm={2}>
                                        <Grid container className='row'>
                                            <Grid item sm={12}><span>T150</span></Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item sm={2}>
                                        <Grid container className='row'>
                                            <Grid item sm={12}><span>T100</span></Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item sm={2}>
                                    </Grid>
                                    <Grid item sm={2}>
                                        <Grid container className='row'>
                                            <Grid item sm={12}><span>T250*</span></Grid>
                                        </Grid>
                                        <Grid container className='row'>
                                            <Grid item sm={12}><span>620 CB</span></Grid>
                                        </Grid>
                                        <Grid container className='row'>
                                            <Grid item sm={12}><span>620 MB</span></Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item sm={2}>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </HtmlTooltip>


                        <Grid item md={1} sm={0}>
                        </Grid>

                        <Grid item md={4} sm={12} sx={{paddingLeft: '0px !important', marginLeft: '-10px'}}>
                            <Grid item md={12} className='form-label' sx={{ marginBottom: '10px' }}>
                                <span><FormattedMessage id="wedge.lofts.highest" /></span>
                            </Grid>
                            <Grid container className='form-row-buttons'>


                                <Grid item md={3}>
                                    <Button disableRipple fullWidth name='highestLoft' value='56' variant="outlined" className={`short ${golfer.highestLoft == "56" as Loft ? "selected" : ""}`} onClick={handleLoftButton}>56</Button>
                                </Grid>
                                <Grid item md={3}>
                                    <Button disableRipple fullWidth name='highestLoft' value='58' variant="outlined" className={`short ${golfer.highestLoft == '58' as Loft ? "selected" : ""}`} onClick={handleLoftButton}>58</Button>
                                </Grid>
                                <Grid item md={3}>
                                    <Button disableRipple fullWidth name='highestLoft' value='60' variant="outlined" className={`short ${golfer.highestLoft == "60" as Loft ? "selected" : ""}`} onClick={handleLoftButton}>60</Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid container sx={{ marginTop: '50px' }}>
                        <Grid item sm={12}>
                            <Grid item md={12} className='form-label' sx={{ marginBottom: '10px', minHeight: '30px' }}>
                                {showSpinner && (
                                    <span className="sk-wave" style={{ display: 'inline-flex', marginLeft: '400px', marginTop: '0px' }} >
                                        <span className="sk-wave-rect" style={{ backgroundColor: 'black' }}></span>
                                        <span className="sk-wave-rect" style={{ backgroundColor: 'black' }}></span>
                                        <span className="sk-wave-rect" style={{ backgroundColor: 'black' }}></span>
                                        <span className="sk-wave-rect" style={{ backgroundColor: 'black' }}></span>
                                        <span className="sk-wave-rect" style={{ backgroundColor: 'black' }}></span>
                                    </span>
                                )}
                                {((!showSpinner && showRecommendedLoftsTitle) || showPopup(1)) && (
                                    <span><FormattedMessage id="wedge.lofts.recommended" /></span>
                                )}
                            </Grid>
                        </Grid>
                        {((!showSpinner && showRecommendedLoftsTitle) || showPopup(1)) && (
                            <HtmlTooltip
                                title={
                                    <>
                                        <Typography color="inherit"><FormattedMessage id="quick.start.change.lofts" /></Typography>
                                        <br />
                                        <div style={{ textAlign: 'right' }}>
                                            <Button onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                            <Button onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                        </div>
                                    </>
                                }
                                open={showPopup(1)}
                                placement={'bottom-start'}
                                arrow={false}
                                maxwidth={400}
                            >


                                <Grid container className='form-row-buttons'>
                                    <Grid item md={1}>
                                        <Button disableRipple fullWidth variant="outlined" value='44' className={`short ${isInRecommendedLofts('44') ? 'selected' : ''}`} onClick={handleRecommendedLoftButton}>44</Button>
                                    </Grid>
                                    <Grid item md={1}>
                                        <Button disableRipple fullWidth variant="outlined" value='46' className={`short ${isInRecommendedLofts('46') ? 'selected' : ''}`} onClick={handleRecommendedLoftButton}>46</Button>
                                    </Grid>
                                    <Grid item md={1}>
                                        <Button disableRipple fullWidth variant="outlined" value='48' className={`short ${isInRecommendedLofts('48') ? 'selected' : ''}`} onClick={handleRecommendedLoftButton}>48</Button>
                                    </Grid>
                                    <Grid item md={1}>
                                        <Button disableRipple fullWidth variant="outlined" value='50' className={`short ${isInRecommendedLofts('50') ? 'selected' : ''}`} onClick={handleRecommendedLoftButton}>50</Button>
                                    </Grid>
                                    <Grid item md={1}>
                                        <Button disableRipple fullWidth variant="outlined" value='52' className={`short ${isInRecommendedLofts('52') ? 'selected' : ''}`} onClick={handleRecommendedLoftButton}>52</Button>
                                    </Grid>
                                    <Grid item md={1}>
                                        <Button disableRipple fullWidth variant="outlined" value='54' className={`short ${isInRecommendedLofts('54') ? 'selected' : ''}`} onClick={handleRecommendedLoftButton}>54</Button>
                                    </Grid>
                                    <Grid item md={1}>
                                        <Button disableRipple fullWidth variant="outlined" value='56' className={`short ${isInRecommendedLofts('56') ? 'selected' : ''}`} onClick={handleRecommendedLoftButton}>56</Button>
                                    </Grid>
                                    <Grid item md={1}>
                                        <Button disableRipple fullWidth variant="outlined" value='58' className={`short ${isInRecommendedLofts('58') ? 'selected' : ''}`} onClick={handleRecommendedLoftButton}>58</Button>
                                    </Grid>
                                    <Grid item md={1}>
                                        <Button disableRipple fullWidth variant="outlined" value='60' className={`short ${isInRecommendedLofts('60') ? 'selected' : ''}`} onClick={handleRecommendedLoftButton}>60</Button>
                                    </Grid>
                                </Grid>
                            </HtmlTooltip>
                        )}
                    </Grid>
                </Container>
            </div>
        </>
    )
}
